import { ArrowUpRight, Check } from "lucide-react";
import { useLang, UI } from "@/i18n/lang";
import { SERVICES } from "@/content/site";
import { SERVICE_LANDINGS } from "@/content/serviceLandings";
import { Reveal, ParallaxImage } from "@/lib/motion";
import { Seo, ORIGIN } from "@/components/Seo";
import { LocaleLink as Link, useLocaleRoute, localizePath, coreAlternates } from "@/i18n/routes";

// Map the on-page service sections to their dedicated SEO landing pages.
const SERVICE_TO_LANDING = {
  "website-creation": "website",
  "app-development": "app",
  "digital-products": "digital-product",
  "book-kdp": "kdp",
};

export default function Services({ locale = "de" }) {
  const { t } = useLang();
  useLocaleRoute(locale, localizePath("/services", locale === "de" ? "en" : "de"));
  const landingOf = (id) => {
    const key = SERVICE_TO_LANDING[id];
    const l = key && SERVICE_LANDINGS.find((s) => s.key === key);
    return l ? l[locale] : null;
  };
  return (
    <div data-testid="services-page">
      <Seo
        title={UI.seo.services.title[locale]}
        description={UI.seo.services.desc[locale]}
        path={localizePath("/services", locale)}
        lang={locale}
        image={`${ORIGIN}/og-services.jpg`}
        alternates={coreAlternates("/services")}
      />
      <section className="pt-36 md:pt-44 pb-16 border-b border-border">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <Reveal>
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-bronze">— {t(UI.services.title)}</span>
            <h1 className="mt-6 font-serif text-5xl md:text-7xl lg:text-8xl tracking-tight text-ink leading-[0.98] max-w-4xl">
              {t(UI.services.intro)}
            </h1>
          </Reveal>
        </div>
      </section>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
        {SERVICES.map((s, i) => (
          <section
            key={s.id}
            data-testid={`service-${s.id}`}
            className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 py-20 md:py-28 border-b border-border items-center"
          >
            <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2 lg:col-start-8" : ""}`}>
              <Reveal>
                <ParallaxImage src={s.image} alt={t(s.title)} className="w-full aspect-[4/3] bg-secondary" strength={24} />
              </Reveal>
            </div>
            <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-1 lg:col-start-1" : "lg:col-start-7"}`}>
              <Reveal>
                <span className="font-serif italic text-5xl text-bronze">0{i + 1}</span>
                <h2 className="mt-3 font-serif text-4xl md:text-5xl tracking-tight text-ink">{t(s.title)}</h2>

                <div className="mt-8 space-y-6">
                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-widest text-bronze mb-2">{t(UI.services.build)}</p>
                    <p className="text-base text-stone">{t(s.build)}</p>
                  </div>
                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-widest text-bronze mb-2">{t(UI.services.who)}</p>
                    <p className="text-base text-stone">{t(s.who)}</p>
                  </div>
                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-widest text-bronze mb-3">{t(UI.services.deliver)}</p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {t(s.deliver).map((d, k) => (
                        <li key={k} className="flex items-center gap-2.5 text-sm text-ink">
                          <Check size={15} className="text-bronze shrink-0" /> {d}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-3">
                  <Link
                    to="/contact"
                    data-testid={`service-cta-${s.id}`}
                    className="inline-flex items-center gap-2 rounded-full bg-bronze text-paper px-7 py-3.5 text-sm font-semibold hover:bg-[#f0be6e] transition-colors duration-300"
                  >
                    {t(UI.cta.startProject)} <ArrowUpRight size={15} />
                  </Link>
                  {landingOf(s.id) && (
                    <Link
                      to={landingOf(s.id).path}
                      data-testid={`service-learnmore-${s.id}`}
                      className="inline-flex items-center gap-1.5 font-mono text-[12px] uppercase tracking-widest text-stone hover:text-bronze transition-colors"
                    >
                      {locale === "de" ? "Mehr erfahren" : "Learn more"} <ArrowUpRight size={13} />
                    </Link>
                  )}
                </div>
              </Reveal>
            </div>
          </section>
        ))}

        {/* Dedicated service pages — internal links for crawlability & SEO */}
        <section data-testid="service-landings-links" className="py-16 md:py-20 border-t border-border">
          <p className="font-mono text-[11px] uppercase tracking-widest text-bronze mb-8">
            {locale === "de" ? "Leistungen im Detail" : "Services in detail"}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-5">
            {SERVICE_LANDINGS.map((s) => (
              <Link
                key={s.key}
                to={s[locale].path}
                data-testid={`services-landing-link-${s.key}`}
                className="group flex items-center justify-between gap-4 border-b border-border pb-4 text-ink hover:text-bronze transition-colors"
              >
                <span className="font-serif text-2xl md:text-3xl">{s[locale].h1}</span>
                <ArrowUpRight size={20} className="shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
